import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ThemeMode } from '../constants';
import { UserPreference } from '../models';
import { LocalStorageService } from './local-storage.service';
import { UserPreferenceService } from './user-preference.service';

/**
 * The Light / Dark / Match Windows appearance.
 *
 * Modelled on LanguageService (the other per-user preference mirrored in localStorage), with two
 * deliberate differences:
 *   - it NEVER reloads the page - the appearance is CSS variables plus one attribute, so the
 *     screen can change under the user's hands;
 *   - it does not write a preference row for a user who has never chosen. Light is the default
 *     (PRD D-7), and a user who never opens the menu should not acquire a stored preference.
 *
 * The attribute this writes, `data-bs-theme`, is Bootstrap 5.3's own colour-mode switch: Bootstrap
 * is imported prebuilt, so its dark rules are already in the bundle. Our own surfaces follow the
 * `--tms-*` variables in tms-lib/styles/_theme.scss, which key off the same attribute.
 *
 * ⚠️ Light removes the attribute rather than setting `light`, so a user who never chooses gets a
 * page identical to the one before this feature existed.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly document = inject(DOCUMENT);

  /** ⚠️ Also hard-coded in each app's index.html inline script — change both together. */
  static readonly ThemeKey = 'theme';
  static readonly DefaultMode = ThemeMode.light;

  private readonly systemPrefersDark = signal(false);

  readonly mode = signal<ThemeMode>(ThemeService.DefaultMode);

  readonly isDark = computed(
    () => this.mode() === ThemeMode.dark || (this.mode() === ThemeMode.system && this.systemPrefersDark()),
  );

  /** The AG Grid theme class. Both themes ship inside the ag-theme-balham.css we already import. */
  readonly gridThemeClass = computed(() => (this.isDark() ? 'ag-theme-balham-dark' : 'ag-theme-balham'));

  constructor() {
    this.watchSystemPreference();
    effect(() => this.applyToDocument(this.isDark()));
  }

  /**
   * Called from each app's APP_INITIALIZER, before anything is authenticated: adopts the mode
   * index.html already applied from localStorage, so the service and the page agree from the
   * first frame — including on the login screen, where there is no user to read a preference for.
   */
  applyStored() {
    this.mode.set(this.storedMode());
  }

  /**
   * Reconciles the local mode with the preference stored against the USER, so someone who chose
   * Dark on another PC sees Dark here (PRD R-3, AC-5).
   *
   * ⚠️ Called once the user is authenticated (from the login menu), not from the app initializer:
   * the request needs a token, and at start-up there may be no user yet.
   */
  load(): Observable<ThemeMode> {
    this.applyStored();

    return this.userPreferenceService.get(ThemeService.ThemeKey).pipe(
      map((userPreference: UserPreference) => this.toMode(userPreference?.value)),
      tap((mode: ThemeMode) => {
        if (mode) {
          this.setLocal(mode);
        }
      }),
      map(() => this.mode()),
      // An appearance is never worth failing the start-up for: fall back to what localStorage had.
      catchError(() => of(this.mode())),
    );
  }

  /** The user picked an appearance: apply it, remember it here, and save it against the user. */
  setMode(mode: ThemeMode): Observable<unknown> {
    this.setLocal(mode);
    return this.userPreferenceService.set(ThemeService.ThemeKey, mode);
  }

  private setLocal(mode: ThemeMode) {
    this.mode.set(mode);
    this.localStorageService.setString(ThemeService.ThemeKey, mode);
  }

  private storedMode(): ThemeMode {
    return this.toMode(this.localStorageService.getString(ThemeService.ThemeKey)) ?? ThemeService.DefaultMode;
  }

  private toMode(value: string): ThemeMode {
    return Object.values(ThemeMode).find((mode: ThemeMode) => mode === value);
  }

  private applyToDocument(isDark: boolean) {
    const element = this.document.documentElement;
    if (isDark) {
      element.setAttribute('data-bs-theme', 'dark');
    } else {
      element.removeAttribute('data-bs-theme');
    }
  }

  private watchSystemPreference() {
    const view = this.document.defaultView;
    if (!view?.matchMedia) {
      return;
    }

    const query = view.matchMedia('(prefers-color-scheme: dark)');
    this.systemPrefersDark.set(query.matches);
    // Match Windows has to follow a change made while the app is open, not only at start-up.
    query.addEventListener('change', (event: MediaQueryListEvent) => this.systemPrefersDark.set(event.matches));
  }
}
